import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, type Point } from '@rydres/shared';
import { Badge, Button, Confirm, MapPreview, Notice, Spinner, fmtDateTime, fmtPhone, formatUsd, useRoute } from '@rydres/ui';
import { api, geoApi } from '../lib/api.js';
import type { PublicBookingView } from '../lib/types.js';
import { AppBadges, appLinksShown } from './AppBadges.js';
import { Backups } from './Backups.js';

export const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'muted'> = {
  requested: 'info',
  quoted: 'info',
  confirmed: 'ok',
  completed: 'ok',
  cancelled: 'muted',
  no_show: 'danger',
  expired: 'muted',
};

export function StatusPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const [view, setView] = useState<PublicBookingView | null | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    api.call<PublicBookingView>('GET', `/public/bookings/${token}`, { auth: false }).then(setView, () => setView(null));

  useEffect(() => {
    void load();
    // Right after paying, the webhook may land a few seconds later: poll briefly.
    if (params.get('paid') || params.get('redirect_status')) {
      let n = 0;
      const timer = setInterval(() => {
        void load();
        if (++n >= 15) clearInterval(timer);
      }, 2000);
      return () => clearInterval(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (view === undefined) return <div className="flex min-h-screen items-center justify-center"><Spinner /></div>;
  if (view === null) return <div className="p-6 text-muted">We could not find that booking.</div>;
  const { booking: b, driver: d } = view;
  const awaitingPayment = b.status === 'requested' && b.paymentMode === 'card';

  const cancel = async () => {
    setConfirming(false);
    try {
      setView(await api.call<PublicBookingView>('POST', `/public/bookings/${token}/cancel`, { auth: false }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not cancel.');
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 pb-16 pt-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">Your ride</h1>
        <Badge value={awaitingPayment ? 'awaiting payment' : b.status} tone={awaitingPayment ? 'warn' : STATUS_TONE[b.status]} />
      </div>
      <p className="mt-1 text-sm text-ink2">with {d.displayName} · {d.legalName} · CPUC permit {d.tcpNumber}</p>

      {awaitingPayment && <div className="mt-4"><Notice tone="warn">Waiting for your payment to be confirmed. This usually takes a few seconds.</Notice></div>}
      {b.status === 'requested' && b.paymentMode !== 'card' && (
        <div className="mt-4"><Notice tone="info">{b.kind === 'custom' ? `${d.displayName} has your request and will text you a price.` : `${d.displayName} has your request and will confirm shortly. You will get a text.`}</Notice></div>
      )}
      {b.status === 'quoted' && (
        <div className="mt-4"><Notice tone="info">{d.displayName} sent you a price of {formatUsd(b.priceCents ?? 0)}. Check your texts for the link to accept it.</Notice></div>
      )}
      {b.status === 'expired' && <div className="mt-4"><Notice tone="warn">This request expired without a confirmed price.{d.handle && <> You can <a className="underline" href={`/${d.handle}`}>ask again</a>.</>}</Notice></div>}
      {b.status === 'cancelled' && <div className="mt-4"><Notice tone="warn">This booking was cancelled{b.cancelReason ? `: ${b.cancelReason}` : '.'}</Notice></div>}
      {b.status === 'cancelled' && b.cancelledBy === 'driver' && <Backups list={d.backups} driverName={d.displayName} title={`Still need a ride? Drivers ${d.displayName ?? 'your driver'} trusts`} />}

      <RideMap from={b.pickupPoint} to={b.dropoffPoint} />
      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 rounded-2xl border border-line bg-card p-4 text-sm">
        <dt className="text-muted">Pickup</dt>
        <dd className="font-bold">{fmtDateTime(b.startsAt)}<div className="font-medium text-ink2">{b.pickupAddress}</div></dd>
        <dt className="text-muted">Drop-off</dt>
        <dd className="font-medium">{b.dropoffAddress}</dd>
        <dt className="text-muted">Passengers</dt>
        <dd className="font-medium">{b.passengers}</dd>
        <dt className="text-muted">Price</dt>
        <dd className="font-bold">
          {b.priceCents == null ? (
            <span className="font-medium text-ink2">Not set yet. {d.displayName} will text you a price.</span>
          ) : (
            formatUsd(b.priceCents)
          )}
          <div className="font-medium text-ink2">
            {b.priceCents == null
              ? ''
              : b.paymentMode === 'card'
              ? b.payment?.status === 'succeeded'
                ? 'Paid by card'
                : b.payment?.status === 'refunded'
                  ? 'Refunded'
                  : b.payment?.status === 'partially_refunded'
                    ? `Refunded ${formatUsd(b.payment.refundedCents)}`
                    : 'Card'
              : `Pay the driver directly${d.zelleHandle ? ` · Zelle ${d.zelleHandle}` : ''}`}
          </div>
        </dd>
        <dt className="text-muted">Driver</dt>
        <dd className="font-medium">
          <a className="text-accent-text underline" href={`tel:${d.phone ?? ''}`}>{fmtPhone(d.phone)}</a>
          {d.handle && (b.status === 'requested' || b.status === 'quoted' || b.status === 'confirmed') && (
            <> · <Link className="text-accent-text underline" to={`/me/people?with=${encodeURIComponent(d.handle)}`}>Message {d.displayName ?? 'the driver'}</Link></>
          )}
        </dd>
      </dl>

      <div className="mt-4 space-y-2">
        {b.payment?.receiptUrl && (
          <a className="block text-sm text-accent-text underline" href={b.payment.receiptUrl} target="_blank" rel="noreferrer">Card receipt</a>
        )}
        {b.waybillUrl && (
          <a className="block text-sm text-accent-text underline" href={b.waybillUrl} target="_blank" rel="noreferrer">Trip record (waybill)</a>
        )}
      </div>

      {(b.status === 'requested' || b.status === 'confirmed') && (
        <div className="mt-8">
          <p className="mb-2 text-xs text-muted">{d.cancelPolicyText}</p>
          <Button variant="danger" className="w-full" onClick={() => setConfirming(true)}>Cancel this ride</Button>
        </div>
      )}
      {error && <div className="mt-3"><Notice tone="danger">{error}</Notice></div>}
      {confirming && (
        <Confirm title="Cancel this ride?" body={d.cancelPolicyText} confirmLabel="Yes, cancel" danger onConfirm={() => void cancel()} onCancel={() => setConfirming(false)} />
      )}
      {appLinksShown() && b.status !== 'cancelled' && b.status !== 'expired' && (
        <section className="mt-8 space-y-3 rounded-2xl border border-line bg-card p-4">
          <h2 className="font-bold">Follow this ride in the {BRAND.name} app</h2>
          <p className="text-sm text-ink2">
            Updates arrive as notifications instead of texts, you can message {d.displayName ?? 'your driver'}, and booking again takes two taps. Sign in with the mobile number you booked with.
          </p>
          <AppBadges />
        </section>
      )}
      <footer className="mt-12 text-center text-xs text-muted">Powered by {BRAND.name}, operated by {BRAND.operator}. The driver provides the transportation.</footer>
    </div>
  );
}

/** Pickup and drop-off on a small map, with the road between them, when the places were picked on the map. */
function RideMap({ from, to }: { from: Point | null; to: Point | null }) {
  const route = useRoute(geoApi, from, to);
  if (!from && !to) return null;
  return <MapPreview className="mt-6" from={from} to={to} line={route?.polyline} />;
}
